import { ConfigValueType } from "./types";
import { ConfigurationMetaValidationError } from "./errors";
import { compileConfigValuePattern } from "./configurationMetaValidation";
const RESERVED_CHARACTERS = [",", ":", "(", ")"];
/** Flattens a ConfigurationMeta's grouping-set tree into one array of every
 * variable it contains (top-level and comprising-type rows alike). */
export function flattenConfigurationMetaVariables(meta) {
    const result = [];
    const visit = (set) => {
        result.push(...set.variables);
        set.groupingSets.forEach(visit);
    };
    meta.groupingSets.forEach(visit);
    return result;
}
function findElementVariable(variables, parent, idx) {
    const match = variables.find((v) => v.parentConfig === parent.id &&
        (idx === undefined ? v.parentConfigIdx === undefined : v.parentConfigIdx === idx));
    if (!match) {
        throw new ConfigurationMetaValidationError(idx === undefined
            ? `"${parent.key}" has no element type defined`
            : `"${parent.key}" has no element type defined at tuple index ${idx}`);
    }
    return match;
}
/** Validates one leaf (STRING/NUMBER/BOOL) value against its metadata.
 *
 * "" is a legitimate value only for a STRING that opts in via `allowEmpty`.
 * Without that opt-in it's rejected for an ARRAY/TUPLE element (which only
 * exists because a user added it through the value editor) and for a STRING
 * that declares permittedValues or a pattern (whose author has enumerated
 * what a value may be, and "" isn't among it -- authoring-time validation
 * blocks "" from reaching permittedValues unless allowEmpty is set). An
 * otherwise-unconstrained top-level leaf still passes "" through, mirroring
 * smithers' validateConfigValue: buildConfigDefaults deliberately seeds ""
 * for a config-meta key that has no default yet (e.g. a newly added
 * parameter), so that's the not-yet-defaulted sentinel, not a value. */
function validateLeafValue(variable, value) {
    const key = variable.key;
    const isString = variable.valueType === ConfigValueType.STRING;
    if (value === "") {
        if (isString && variable.allowEmpty) {
            return;
        }
        if (variable.parentConfig) {
            throw new ConfigurationMetaValidationError(`"${key}" must not be empty`);
        }
        if (isString && (variable.permittedValues?.length || variable.pattern)) {
            throw new ConfigurationMetaValidationError(`"${key}" must not be empty`);
        }
        return;
    }
    switch (variable.valueType) {
        case ConfigValueType.NUMBER: {
            const numericValue = Number(value);
            if (Number.isNaN(numericValue)) {
                throw new ConfigurationMetaValidationError(`"${key}" must be a number`);
            }
            if (!variable.allowDecimal && !Number.isInteger(numericValue)) {
                throw new ConfigurationMetaValidationError(`"${key}" must be a whole number`);
            }
            if (variable.minValue !== undefined && numericValue < variable.minValue) {
                throw new ConfigurationMetaValidationError(`"${key}" must be at least ${variable.minValue}`);
            }
            if (variable.maxValue !== undefined && numericValue > variable.maxValue) {
                throw new ConfigurationMetaValidationError(`"${key}" must be at most ${variable.maxValue}`);
            }
            return;
        }
        case ConfigValueType.BOOL:
            if (value !== "true" && value !== "false") {
                throw new ConfigurationMetaValidationError(`"${key}" must be true or false`);
            }
            return;
        case ConfigValueType.STRING:
            if (variable.pattern && !compileConfigValuePattern(variable.pattern).test(value)) {
                throw new ConfigurationMetaValidationError(`"${key}" must match the pattern ${variable.pattern}`);
            }
            if (variable.permittedValues?.length && !variable.permittedValues.includes(value)) {
                throw new ConfigurationMetaValidationError(`"${key}" must be one of: ${variable.permittedValues.join(", ")}`);
            }
            return;
        default:
            throw new ConfigurationMetaValidationError(`"${key}" is not a leaf type (STRING, NUMBER, or BOOL)`);
    }
}
/** Recursively validates and serializes `value` against `variable`'s
 * declared type. `allVariables` is the full flat list for the layer, used to
 * look up an ARRAY/TUPLE's element metadata by parentConfig. */
export function serializeValue(variable, value, allVariables) {
    if (variable.valueType === ConfigValueType.ARRAY) {
        if (!Array.isArray(value)) {
            throw new ConfigurationMetaValidationError(`"${variable.key}" is an ARRAY and requires an array of values`);
        }
        const elementVariable = findElementVariable(allVariables, variable);
        const serialized = value
            .map((element) => {
            const serializedElement = serializeValue(elementVariable, element, allVariables);
            // Parenthesize a tuple element so it stays one unit inside the comma-joined array.
            return elementVariable.valueType === ConfigValueType.TUPLE
                ? `(${serializedElement})`
                : serializedElement;
        })
            .join(",");
        // A single element that itself serializes to "" is indistinguishable from
        // an empty array (parseConfigurationValue treats "" as [] unconditionally)
        // -- reject it rather than silently losing the element on the next read.
        if (value.length === 1 && serialized === "") {
            throw new ConfigurationMetaValidationError(`"${variable.key}" cannot have a single empty element -- it would be indistinguishable from an empty array`);
        }
        return serialized;
    }
    if (variable.valueType === ConfigValueType.TUPLE) {
        if (!Array.isArray(value) || value.length !== 2) {
            throw new ConfigurationMetaValidationError(`"${variable.key}" is a TUPLE and requires exactly two values`);
        }
        const first = serializeValue(findElementVariable(allVariables, variable, 0), value[0], allVariables);
        const second = serializeValue(findElementVariable(allVariables, variable, 1), value[1], allVariables);
        return `${first}:${second}`;
    }
    if (Array.isArray(value)) {
        throw new ConfigurationMetaValidationError(`"${variable.key}" is not an ARRAY or TUPLE and cannot take an array of values`);
    }
    validateLeafValue(variable, value);
    if (variable.parentConfig) {
        for (const char of RESERVED_CHARACTERS) {
            if (value.includes(char)) {
                throw new ConfigurationMetaValidationError(`"${variable.key}" value "${value}" may not contain "${char}" -- reserved for ARRAY/TUPLE serialization`);
            }
        }
    }
    return value;
}
/**
 * Builds the flat configuration map to send to the backend from
 * caller-collected values, keyed by each configuration key. A key whose
 * ConfigurationMetaVariable is ARRAY or TUPLE takes a nested ConfigValue
 * (validated and serialized per the type's comprising type(s) -- comma-joined
 * for ARRAY, colon-joined for TUPLE, with a tuple element of an array
 * parenthesized to stay distinct); a key with no configuration meta defined
 * passes through unvalidated, as today. Either way, only top-level keys ever
 * appear in the result -- an ARRAY/TUPLE's comprising-type row(s) are UX
 * scaffolding and are elided here, never persisted as their own keys.
 */
export function buildConfigurationValues(variables, values) {
    const comprisingTypeKeys = new Set(variables.filter((v) => v.parentConfig).map((v) => v.key));
    // First occurrence wins on a duplicate top-level key (e.g. the same key
    // recurring across multiple selected layers) -- matches smithers'
    // documented first-layer precedence, so this doesn't silently validate
    // against a different layer's metadata than the one the editor used.
    const topLevelByKey = new Map();
    for (const v of variables) {
        if (!v.parentConfig && !topLevelByKey.has(v.key)) {
            topLevelByKey.set(v.key, v);
        }
    }
    const result = {};
    for (const [key, value] of Object.entries(values)) {
        if (comprisingTypeKeys.has(key)) {
            // A comprising-type row's key/value is UX scaffolding for its parent
            // ARRAY/TUPLE, not a real configuration key -- elide it defensively
            // even if a caller passed one directly.
            continue;
        }
        const variable = topLevelByKey.get(key);
        if (!variable) {
            if (typeof value !== "string") {
                throw new ConfigurationMetaValidationError(`"${key}" has no configuration meta defined and must be a plain string`);
            }
            result[key] = value;
            continue;
        }
        result[key] = serializeValue(variable, value, variables);
    }
    return result;
}
function splitTopLevel(text, delimiter, key) {
    const parts = [];
    let depth = 0;
    let current = "";
    for (const char of text) {
        if (char === "(")
            depth++;
        if (char === ")") {
            depth--;
            if (depth < 0) {
                throw new ConfigurationMetaValidationError(`"${key}" value "${text}" has an unmatched ")"`);
            }
        }
        if (char === delimiter && depth === 0) {
            parts.push(current);
            current = "";
        }
        else {
            current += char;
        }
    }
    if (depth !== 0) {
        throw new ConfigurationMetaValidationError(`"${key}" value "${text}" has an unmatched "("`);
    }
    parts.push(current);
    return parts;
}
function unwrapParens(text) {
    return text.startsWith("(") && text.endsWith(")") ? text.slice(1, -1) : text;
}
/**
 * The inverse of `serializeValue`: parses one top-level key's stored string
 * value back into the structured ConfigValue shape `buildConfigurationValues`
 * accepts, for populating an edit form. `allVariables` is the full flat list
 * for the layer, as in `buildConfigurationValues`.
 */
export function parseConfigurationValue(variable, serialized, allVariables) {
    if (variable.valueType === ConfigValueType.ARRAY) {
        if (serialized === "")
            return [];
        const elementVariable = findElementVariable(allVariables, variable);
        return splitTopLevel(serialized, ",", variable.key).map((part) => {
            const unwrapped = elementVariable.valueType === ConfigValueType.TUPLE ? unwrapParens(part) : part;
            return parseConfigurationValue(elementVariable, unwrapped, allVariables);
        });
    }
    if (variable.valueType === ConfigValueType.TUPLE) {
        const parts = splitTopLevel(serialized, ":", variable.key);
        if (parts.length !== 2) {
            throw new ConfigurationMetaValidationError(`"${variable.key}" value "${serialized}" is not a valid tuple (expected exactly one ":")`);
        }
        const first = parseConfigurationValue(findElementVariable(allVariables, variable, 0), parts[0], allVariables);
        const second = parseConfigurationValue(findElementVariable(allVariables, variable, 1), parts[1], allVariables);
        return [first, second];
    }
    return serialized;
}
//# sourceMappingURL=configValueSerialization.js.map